import { useCallback, useEffect, useState } from "react";
import type { GridPos } from "@reagent/sim";
import { nodeAt, useGameStore, nextValveState } from "../app/gameStore";
import { useInteractionStore } from "../render/iso/interactionStore";
import { getCardView } from "./cards/cardViews";
import { playCue } from "../audio/AudioEngine";
import "./keyboard.css";

/**
 * Playing Reagent without a pointer.
 *
 * Every action in the game was previously a drag or a click on a 3D canvas,
 * which is the least accessible input there is: it needs a mouse, fine motor
 * control, and the ability to see where the cursor is over a projected grid.
 * None of that is necessary — the game is turn-based, the board is a small
 * integer grid, and every command already exists as a store action.
 *
 * So this drives those same actions from the keyboard, and deliberately
 * reuses `hoveredTile` as its cursor rather than inventing a second one. The
 * placement ghost, the tile highlight and the drop hint then all light up for
 * the keyboard exactly as they do for the mouse — one aiming system, not two
 * that can disagree. Whichever input moved last owns the cursor, which is the
 * right answer for someone using both.
 */

/** Where the cursor starts, and where it returns after a stage change. */
function centreOf(cols: number, rows: number): GridPos {
  return { x: Math.floor(cols / 2), y: Math.floor(rows / 2) };
}

export function KeyboardControl() {
  const snapshot = useGameStore((s) => s.snapshot);
  const setHoveredTile = useInteractionStore((s) => s.setHoveredTile);
  const setDraggedCard = useInteractionStore((s) => s.setDraggedCard);
  const pendingInstallId = useGameStore((s) => s.pendingInstall?.cardId ?? null);

  const [selected, setSelected] = useState<number | null>(null);
  const [cursor, setCursor] = useState<GridPos>(() => centreOf(snapshot.cols, snapshot.rows));
  /** Set once the player actually uses a key, so the hint is not always up. */
  const [engaged, setEngaged] = useState(false);

  const clearSelection = useCallback(() => {
    setSelected(null);
    setDraggedCard(null, null);
  }, [setDraggedCard]);

  // A new stage is a new board, and possibly a smaller one: an off-board
  // cursor would aim the ghost at a tile that no longer exists.
  useEffect(() => {
    setCursor(centreOf(snapshot.cols, snapshot.rows));
    clearSelection();
  }, [snapshot.stageId, snapshot.cols, snapshot.rows, clearSelection]);

  // Setup can be armed from the panel as well as from 1-9, and the two must
  // not disagree: a piece picked with the mouse has to be placeable with
  // enter, and the hint below has to name it. The store's pendingInstall is the
  // one source of truth; this mirrors it back into the key handler's index
  // rather than letting a second selection live here.
  useEffect(() => {
    if (snapshot.phase !== "setup") return;
    const index = pendingInstallId
      ? snapshot.setupCatalogue.findIndex((o) => o.cardId === pendingInstallId)
      : -1;
    setSelected(index === -1 ? null : index);
  }, [pendingInstallId, snapshot.phase, snapshot.setupCatalogue]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Never steal keys from a text box — the tuning console has a seed input
      // and the settings panel has sliders.
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const state = useGameStore.getState();
      const shot = state.snapshot;
      const canPlay = shot.phase === "play" && !shot.isRunComplete && !shot.isRunFailed;
      const inSetup = shot.phase === "setup";

      // The next tile is computed from the cursor this handler closed over
      // rather than inside a state updater: a React updater must be pure, and
      // writing to another store from inside one warns (rightly) about
      // updating a component while a different one renders.
      const move = (dx: number, dy: number) => {
        setEngaged(true);
        const next = {
          x: Math.min(Math.max(cursor.x + dx, 0), shot.cols - 1),
          y: Math.min(Math.max(cursor.y + dy, 0), shot.rows - 1),
        };
        setCursor(next);
        setHoveredTile(next);
      };

      switch (event.key) {
        case "ArrowLeft":
          move(-1, 0);
          break;
        case "ArrowRight":
          move(1, 0);
          break;
        case "ArrowUp":
          move(0, -1);
          break;
        case "ArrowDown":
          move(0, 1);
          break;

        case "Escape":
          state.cancelPipe();
          if (inSetup) state.selectSetupOffer(null);
          clearSelection();
          setHoveredTile(null);
          return;

        case "r":
        case "R":
          if (shot.phase === "play" && !shot.isRunComplete && !shot.isRunFailed) {
            playCue("switchThrow");
            state.startRun();
          }
          return;

        case "v":
        case "V": {
          if (!canPlay || selected !== null || state.pendingPipe) return;
          setEngaged(true);
          const node = nodeAt(shot, cursor);
          if (!node || node.kind !== "flowMover") return;
          const outgoingEdgeIds = shot.edges
            .filter((e) => e.fromNodeId === node.id)
            .map((e) => e.id);
          state.setValveAt(node.id, nextValveState(node.valveState, outgoingEdgeIds));
          return;
        }

        // Setup has no card to place — just a piece of bought hardware, a
        // machine or a pipe, to pull back out.
        case "Delete":
        case "Backspace": {
          if (!inSetup) return;
          setEngaged(true);
          state.dismantleAt(cursor);
          return;
        }

        case "Enter":
        case " ": {
          setEngaged(true);

          // Status has nothing to aim at — it is read, then dismissed.
          if (shot.phase === "status") {
            state.advanceFromStatus();
            break;
          }

          // Setup routes through clickTile, which owns buy-and-place — and a
          // bought pipe's two taps, so the selection survives the first.
          if (inSetup) {
            if (selected === null) return;
            state.clickTile(cursor);
            if (useGameStore.getState().pendingInstall === null) clearSelection();
            break;
          }

          // A pipe waiting for its far end takes priority: the cursor is
          // already aimed and the card is already committed.
          if (state.pendingPipe) {
            state.clickTile(cursor);
            clearSelection();
            break;
          }
          if (selected === null || !canPlay) return;

          const instance = shot.hand[selected];
          if (!instance) return;
          const view = getCardView(instance.cardId);
          if (!view) return;

          if (view.target === "instant") {
            state.playInstant(instance.instanceId);
          } else if (view.target === "pipe") {
            const node = nodeAt(shot, cursor);
            if (node) state.beginPipe(instance.instanceId, node.id);
            else state.reject("a pipe has to start at a machine");
          } else {
            state.playCardAt(instance.instanceId, cursor);
          }
          clearSelection();
          break;
        }

        default: {
          // 1-9 pick the nth card in hand — or, in Setup, the nth catalogue
          // offer. Pressing the selected one again deselects it, so there is
          // always a way out without Escape.
          const index = Number(event.key) - 1;
          if (!Number.isInteger(index) || index < 0 || index > 8) return;
          setEngaged(true);

          if (inSetup) {
            const offer = shot.setupCatalogue[index];
            if (!offer) return;
            if (selected === index) {
              setSelected(null);
              state.selectSetupOffer(null);
              return;
            }
            playCue("cardPick");
            setSelected(index);
            state.selectSetupOffer(offer.cardId);
            setHoveredTile(cursor);
            break;
          }
          if (!canPlay) return;

          const instance = shot.hand[index];
          const view = instance ? getCardView(instance.cardId) : null;
          if (!view) return;

          if (selected === index) {
            clearSelection();
            return;
          }
          playCue("cardPick");
          setSelected(index);
          setDraggedCard(view.deploys ?? view.category, view.target);
          setHoveredTile(cursor);
          break;
        }
      }

      // Only reached by keys this component actually handled.
      event.preventDefault();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cursor, selected, clearSelection, setDraggedCard, setHoveredTile]);

  const selectedId =
    selected === null
      ? null
      : snapshot.phase === "setup"
        ? (snapshot.setupCatalogue[selected]?.cardId ?? null)
        : snapshot.hand[selected]?.cardId;
  const selectedView = selectedId ? getCardView(selectedId) : null;

  if (!engaged) return null;

  return (
    <div className="keys" role="status">
      <span className="keys__cursor">
        {cursor.x},{cursor.y}
      </span>
      {snapshot.phase === "status" ? (
        <span>enter or space to continue</span>
      ) : selectedView ? (
        <span>
          <strong>{selectedView.name}</strong> —{" "}
          {snapshot.phase === "setup"
            ? "arrows to aim, enter to place"
            : selectedView.target === "instant"
              ? "enter to play"
              : selectedView.target === "pipe"
                ? "enter on a machine to start the pipe"
                : "arrows to aim, enter to place"}
        </span>
      ) : (
        <span>
          {snapshot.phase === "setup"
            ? "arrows aim · 1–9 pick an offer · enter places · backspace dismantles"
            : "arrows aim · 1–9 pick a card · enter places · v cycles valve · r runs · esc cancels"}
        </span>
      )}
    </div>
  );
}
